import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { HOODIE_API_BASE_URL } from "../../api/hoodieClient";
import {
  isMlistApiConfigured,
  isMlistApiValid,
  MLIST_API_BASE_URL,
} from "../../api/mlistClient";
import { getUserRelationshipGraph } from "../../api/services/adminToolsBridgeApi";
import {
  getHoodieById,
  getListingById,
  getMyProfile,
  getUserById,
  isHoodieApiConfigured,
} from "../../api/services/hoodieApi";
import {
  displayValue,
  extractRecommendationListingIds,
  formatApiError,
  formatUserLabel,
  HOODIE_PROFILE_SERIALIZER_FIELDS,
  relationshipGraphBridgeMessage,
  resolveLegacyUserId,
  USER_SERIALIZER_FIELDS,
} from "./hoodieProfileWranglerUtils";
import AdminToolsMlistAuthStatus from "./AdminToolsMlistAuthStatus";

const Wrap = styled.div`
  width: 100%;
  padding: 24px 16px;
`;

const Card = styled.div`
  border: 1px solid #e5e5e5;
  border-radius: 10px;
  padding: 16px;
  background: #fff;
  margin-top: 12px;
`;

const Banner = styled.div`
  border-radius: 10px;
  padding: 10px 12px;
  border: 1px solid ${(p) => (p.$kind === "error" ? "#f1b7b7" : "#f0e0a8")};
  background: ${(p) => (p.$kind === "error" ? "#fff5f5" : "#fffbeb")};
  color: ${(p) => (p.$kind === "error" ? "#7a1c1c" : "#6b5700")};
  margin-top: 12px;
  font-size: 13px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 160px 1fr;
  gap: 8px 12px;
  font-size: 13px;
`;

const Key = styled.div`
  font-weight: 700;
  color: #444;
`;

const Val = styled.div`
  color: #333;
  word-break: break-word;
  white-space: pre-wrap;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 8px;
`;

const Th = styled.th`
  text-align: left;
  font-size: 12px;
  color: #666;
  border-bottom: 1px solid #eee;
  padding: 8px;
`;

const Td = styled.td`
  border-bottom: 1px solid #f0f0f0;
  padding: 8px;
  font-size: 14px;
`;

const Button = styled.button`
  padding: 9px 12px;
  border-radius: 8px;
  border: 1px solid #d0d0d0;
  background: #fff;
  cursor: pointer;
`;

const Muted = styled.div`
  color: #666;
  font-size: 13px;
`;

const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #444;
  margin-top: 14px;
  margin-bottom: 6px;

  &:first-of-type {
    margin-top: 0;
  }
`;

export default function HoodieProfileWranglerDetailPage() {
  const { id: routeId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [profile, setProfile] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [userErr, setUserErr] = useState(null);

  const [favLoading, setFavLoading] = useState(false);
  const [favoriteListings, setFavoriteListings] = useState([]);

  const [graphLoading, setGraphLoading] = useState(false);
  const [graphErr, setGraphErr] = useState(null);
  const [relationshipGraph, setRelationshipGraph] = useState(null);

  const isMeRoute = routeId === "me";
  const endpoint = isMeRoute
    ? "hoodies/me/"
    : `hoodies/${routeId}/`;

  const recommendationIds = useMemo(
    () => extractRecommendationListingIds(profile?.recommendations),
    [profile]
  );

  async function loadRelationshipGraph(userId) {
    if (userId == null) {
      setRelationshipGraph(null);
      setGraphErr(null);
      setGraphLoading(false);
      return;
    }
    if (!isMlistApiConfigured() || !isMlistApiValid()) {
      setRelationshipGraph(null);
      setGraphErr({
        status: 404,
        message: !isMlistApiConfigured()
          ? "mlist-api not configured"
          : "invalid mlist-api base URL",
      });
      setGraphLoading(false);
      return;
    }
    setGraphLoading(true);
    setGraphErr(null);
    try {
      const data = await getUserRelationshipGraph(userId);
      setRelationshipGraph(data);
    } catch (e) {
      setRelationshipGraph(null);
      setGraphErr(e);
    } finally {
      setGraphLoading(false);
    }
  }

  async function loadFavoriteListings(ids) {
    if (!ids.length) {
      setFavoriteListings([]);
      return;
    }
    setFavLoading(true);
    const rows = [];
    await Promise.all(
      ids.map(async (listingId) => {
        try {
          const listing = await getListingById(listingId);
          rows.push({ id: listingId, listing, error: null });
        } catch (e) {
          rows.push({ id: listingId, listing: null, error: formatApiError(e) });
        }
      })
    );
    rows.sort((a, b) => a.id - b.id);
    setFavoriteListings(rows);
    setFavLoading(false);
  }

  async function load() {
    if (!isHoodieApiConfigured()) return;
    setLoading(true);
    setErr(null);
    setUserDetail(null);
    setUserErr(null);
    setFavoriteListings([]);
    setRelationshipGraph(null);
    setGraphErr(null);

    try {
      const p = isMeRoute ? await getMyProfile() : await getHoodieById(routeId);
      setProfile(p);

      const legacyUserId = resolveLegacyUserId(p);
      if (legacyUserId != null) {
        try {
          const u = await getUserById(legacyUserId);
          setUserDetail(u);
        } catch (e) {
          setUserErr(e);
        }
        await loadRelationshipGraph(legacyUserId);
      } else {
        setGraphErr({ message: "No legacy user id on hoodie profile" });
      }

      const ids = extractRecommendationListingIds(p?.recommendations);
      await loadFavoriteListings(ids);
    } catch (e) {
      setProfile(null);
      setErr(e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeId]);

  if (!isHoodieApiConfigured()) {
    return (
      <Wrap>
        <h1>Hoodie profile</h1>
        <Card>Set REACT_APP_HOODIE_API_BASE_URL.</Card>
      </Wrap>
    );
  }

  const profileKeys = profile ? Object.keys(profile).sort() : [];
  const legacyUserId = resolveLegacyUserId(profile);
  const mlistConfigured = isMlistApiConfigured();
  const mlistValid = isMlistApiValid();

  return (
    <Wrap>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
        <div>
          <h1 style={{ margin: 0 }}>
            Hoodie profile {profile?.id != null ? `#${profile.id}` : routeId}
          </h1>
          <div style={{ color: "#666", fontSize: 13, marginTop: 4 }}>
            Read-only v1 • <code>{HOODIE_API_BASE_URL}{endpoint}</code>
          </div>
        </div>
        <Button type="button" onClick={() => navigate(-1)}>
          Back
        </Button>
      </div>

      <div style={{ marginTop: 10 }}>
        <Link to="/admin-tools/hoodies">← Hoodies</Link>
        <span style={{ color: "#999" }}> • </span>
        <Link to="/admin-tools">Admin Tools</Link>
      </div>

      <AdminToolsMlistAuthStatus />

      {loading ? (
        <Card>
          <em>Loading profile…</em>
        </Card>
      ) : err ? (
        <Banner $kind="error">
          <strong>Could not load profile</strong>
          <div>{formatApiError(err)}</div>
          {err.status ? <div>HTTP {err.status}</div> : null}
          <div style={{ marginTop: 8 }}>
            Legacy permissions typically allow only your own profile. Try{" "}
            <Link to="/admin-tools/hoodies/me">/admin-tools/hoodies/me</Link> while
            logged into Django in this browser.
          </div>
        </Banner>
      ) : !profile ? (
        <Card>Unable to load.</Card>
      ) : (
        <>
          <Card>
            <div style={{ fontWeight: 700, marginBottom: 10 }}>
              Profile fields (API response)
            </div>
            <Grid>
              {profileKeys.map((key) => (
                <React.Fragment key={key}>
                  <Key>{key}</Key>
                  <Val>
                    {key === "user"
                      ? formatUserLabel(profile.user)
                      : key === "recommendations"
                      ? `${recommendationIds.length} listing id(s)`
                      : displayValue(profile[key])}
                  </Val>
                </React.Fragment>
              ))}
            </Grid>
            <div style={{ marginTop: 10, fontSize: 12, color: "#666" }}>
              Serializer reference: {HOODIE_PROFILE_SERIALIZER_FIELDS.join(", ")}
            </div>
          </Card>

          <Card>
            <div style={{ fontWeight: 700, marginBottom: 10 }}>
              Connected user
            </div>
            {profile.user ? (
              <>
                <Grid>
                  {USER_SERIALIZER_FIELDS.map((key) => (
                    <React.Fragment key={key}>
                      <Key>{key}</Key>
                      <Val>{displayValue(profile.user[key])}</Val>
                    </React.Fragment>
                  ))}
                </Grid>
                {userDetail ? (
                  <div style={{ marginTop: 10, fontSize: 12, color: "#666" }}>
                    Refreshed via <code>GET users/{profile.user.id}/</code>
                  </div>
                ) : userErr ? (
                  <Banner $kind="warn" style={{ marginTop: 10 }}>
                    <code>GET users/{profile.user.id}/</code> failed:{" "}
                    {formatApiError(userErr)}
                    {userErr.status ? ` (HTTP ${userErr.status})` : ""}. Embedded{" "}
                    <code>user</code> from hoodie profile is still shown above.
                  </Banner>
                ) : null}
              </>
            ) : (
              <Banner $kind="warn">No embedded user on profile response.</Banner>
            )}
          </Card>

          <Card>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>
              Relationship Graph (read-only)
            </div>
            <Muted style={{ marginBottom: 10 }}>
              Served by <code>mlist-api</code> (not the hoodie API):{" "}
              <code>
                {mlistConfigured && legacyUserId != null
                  ? `${MLIST_API_BASE_URL}admin-tools/users/${legacyUserId}/relationship-graph/`
                  : "REACT_APP_MLIST_API_BASE_URL is not set or user id missing"}
              </code>
            </Muted>

            {legacyUserId == null ? (
              <Banner $kind="warn">
                No legacy <code>user.id</code> on the hoodie profile response —
                cannot load the relationship graph.
              </Banner>
            ) : graphLoading ? (
              <Muted>
                <em>Loading relationship graph…</em>
              </Muted>
            ) : graphErr ? (
              (() => {
                const msg = relationshipGraphBridgeMessage(graphErr, {
                  configured: mlistConfigured,
                  valid: mlistValid,
                });
                return (
                  <Banner $kind={msg.kind}>
                    <div style={{ fontWeight: 700 }}>{msg.title}</div>
                    <div>{msg.detail}</div>
                    {graphErr?.status ? (
                      <div style={{ marginTop: 6 }}>HTTP {graphErr.status}</div>
                    ) : null}
                  </Banner>
                );
              })()
            ) : relationshipGraph ? (
              <>
                <SectionTitle>User</SectionTitle>
                <Grid>
                  <Key>id</Key>
                  <Val>{displayValue(relationshipGraph.user?.id)}</Val>
                  <Key>email</Key>
                  <Val>{displayValue(relationshipGraph.user?.email)}</Val>
                  <Key>name</Key>
                  <Val>
                    {relationshipGraph.user
                      ? `${relationshipGraph.user.first_name || ""} ${
                          relationshipGraph.user.last_name || ""
                        }`.trim() || "—"
                      : "—"}
                  </Val>
                  <Key>is_active</Key>
                  <Val>{displayValue(relationshipGraph.user?.is_active)}</Val>
                </Grid>

                <SectionTitle>Member</SectionTitle>
                {relationshipGraph.member ? (
                  <Grid>
                    <Key>member id</Key>
                    <Val>{displayValue(relationshipGraph.member.id)}</Val>
                    <Key>is_club_member</Key>
                    <Val>
                      {displayValue(relationshipGraph.member.is_club_member)}
                    </Val>
                    <Key>is_list_member</Key>
                    <Val>
                      {displayValue(relationshipGraph.member.is_list_member)}
                    </Val>
                  </Grid>
                ) : (
                  <Banner $kind="warn">
                    No classic_site.Member found for this user.
                  </Banner>
                )}

                <SectionTitle>Hoodie Profile</SectionTitle>
                {relationshipGraph.hoodie_profile ? (
                  <Grid>
                    <Key>id</Key>
                    <Val>{displayValue(relationshipGraph.hoodie_profile.id)}</Val>
                    <Key>zip_code</Key>
                    <Val>
                      {displayValue(relationshipGraph.hoodie_profile.zip_code)}
                    </Val>
                  </Grid>
                ) : (
                  <Muted>No HoodieProfile row in legacy DB for this user.</Muted>
                )}

                <SectionTitle>
                  Owned listings via DirectoryEntry.administrator
                </SectionTitle>
                <Muted style={{ marginBottom: 8 }}>
                  Listings this user&apos;s <code>Member</code> account
                  administers — not favorites.
                </Muted>
                {relationshipGraph.owned_listings?.length ? (
                  <Table>
                    <thead>
                      <tr>
                        <Th>Listing id</Th>
                        <Th>Business</Th>
                        <Th>Link</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {relationshipGraph.owned_listings.map((row) => (
                        <tr key={row.id}>
                          <Td>{row.id}</Td>
                          <Td>{row.business_name ?? "—"}</Td>
                          <Td>
                            <Link to={`/admin-tools/listings/${row.id}`}>
                              Open in Listings Wrangler
                            </Link>
                          </Td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <Muted>No owned listings for this member.</Muted>
                )}

                <SectionTitle>
                  Favorites/recommendations, not ownership
                </SectionTitle>
                <Muted style={{ marginBottom: 8 }}>
                  From <code>hoodie_listingrecommendation</code> (positive) in
                  legacy Postgres — not administered listings.
                </Muted>
                {relationshipGraph.recommended_listings?.length ? (
                  <Table>
                    <thead>
                      <tr>
                        <Th>Listing id</Th>
                        <Th>Business</Th>
                        <Th>Link</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {relationshipGraph.recommended_listings.map((row) => (
                        <tr key={row.id}>
                          <Td>{row.id}</Td>
                          <Td>{row.business_name ?? "—"}</Td>
                          <Td>
                            <Link to={`/admin-tools/listings/${row.id}`}>
                              Open in Listings Wrangler
                            </Link>
                          </Td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <Muted>No recommended listings in legacy DB.</Muted>
                )}
              </>
            ) : (
              <Muted>No relationship graph loaded.</Muted>
            )}
          </Card>

          <Card>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>
              Favorites / recommendations ({recommendationIds.length})
            </div>
            <div style={{ fontSize: 13, color: "#666", marginBottom: 8 }}>
              From <code>recommendations</code> on the hoodie API (session) —
              resolved via <code>GET listings/:id/</code> per id. These are{" "}
              <strong>not</strong> owned listings; see Relationship Graph above
              for legacy DB favorites vs owned listings.
            </div>
            {favLoading ? (
              <em>Loading listing details…</em>
            ) : recommendationIds.length === 0 ? (
              <span style={{ color: "#666" }}>None</span>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Listing id</Th>
                    <Th>Business</Th>
                    <Th>Link</Th>
                  </tr>
                </thead>
                <tbody>
                  {favoriteListings.map((row) => (
                    <tr key={row.id}>
                      <Td>{row.id}</Td>
                      <Td>
                        {row.error
                          ? row.error
                          : row.listing?.business_name ?? "—"}
                      </Td>
                      <Td>
                        {row.error ? (
                          "—"
                        ) : (
                          <Link to={`/admin-tools/listings/${row.id}`}>
                            Open in Listings Wrangler
                          </Link>
                        )}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>

          <Card>
            <div style={{ fontWeight: 700, marginBottom: 10 }}>
              API gaps (read-only v1)
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: "#444" }}>
              <li>
                <strong>Owned listings (hoodie API):</strong> Still not on{" "}
                <code>DirectoryEntrySerializer</code>. Use the Relationship Graph
                panel (mlist-api bridge) when configured.
              </li>
              <li>
                <strong>Member link (hoodie API):</strong> No hoodie →{" "}
                <code>classic_site.Member</code> in hoodie responses; bridge
                resolves via <code>auth_user.id</code>.
              </li>
              <li>
                <strong>Hoodie directory list:</strong>{" "}
                <code>GET hoodies/</code> is blocked by{" "}
                <code>IsOwnProfileOrUnauthorized</code> for typical sessions; use{" "}
                <code>hoodies/me/</code> or known ids.
              </li>
              <li>
                <strong>Read-only v1:</strong> No PATCH on this surface.
              </li>
            </ul>
          </Card>
        </>
      )}
    </Wrap>
  );
}
